import { useTranslation } from 'react-i18next'
import IconFileText from '@/shared/icons/NewDesign/IconFileText'
import IconPen from '@/shared/icons/NewDesign/IconPen'
import IconPower from '@/shared/icons/NewDesign/IconPower'
import { statusTranslationKeyMap } from '../../../ContractsTable/contractStatuses'
import { formatContractDate, parseContractDate } from '../../../ContractsTable/formatContractDate'
import { Contract } from '../../../types'
import { resolvePrestationTone, resolveStatusLook } from '../contractStatusTone'

const OFFER_DETAIL_KEYS = ['carGas', 'carElecBase', 'carElecHp', 'carElecHc', 'monthlyAmount'] as const
const NO_PRESTATION = '-1'

const useContractRow = (contract: Contract) => {
  const { t } = useTranslation()
  const { tone, Icon } = resolveStatusLook(contract.status)
  const statusKey = statusTranslationKeyMap[contract.status]
  const prestation = contract.situation && contract.situation !== NO_PRESTATION ? contract.situation : ''

  const steps = [
    { key: 'creation', label: t('contractsTable:creation'), Icon: IconFileText, value: contract.dateCreatedAt },
    { key: 'signature', label: t('contractsTable:signature'), Icon: IconPen, value: contract.dateSignature },
    { key: 'activation', label: t('contractsTable:activation'), Icon: IconPower, value: contract.dateActivation },
  ].map(step => ({
    key: step.key,
    label: step.label,
    Icon: step.Icon,
    text: formatContractDate(step.value, ' · '),
    done: parseContractDate(step.value) !== null,
    author: step.key === 'creation' ? contract.createdBy : null,
  }))

  const offerDetails = contract.energyType
    ? OFFER_DETAIL_KEYS.filter(key => contract[key] !== null && contract[key] !== undefined).map(key => ({
        key,
        label: t(`contractsTable:${key}`),
        value: String(contract[key]),
      }))
    : []

  return {
    status: {
      label: (statusKey ? t(statusKey) : contract.status) || t('subscriptionTable:no_status'),
      tone,
      Icon,
      numberLabel: t('dashboard:overview.contract_number'),
      number: contract.id,
    },
    steps,
    contact: {
      hasContact: Boolean(contract.phone || contract.firstname || contract.lastname),
      emptyLabel: t('contractsTable:no_contact_data'),
      lastname: contract.lastname,
      firstname: contract.firstname,
      email: contract.email,
      phone: contract.phone,
      zohoLink: contract.zohoLink,
      zohoLinkLabel: t('dashboard:overview.open_in_zoho'),
    },
    offer: {
      hasOffer: Boolean(contract.providerName || contract.offer),
      emptyLabel: t('contractsTable:no_offer_data'),
      provider: contract.providerName,
      name: contract.offer,
      prestation,
      prestationTone: resolvePrestationTone(prestation),
      details: offerDetails,
    },
  }
}

export default useContractRow
